import axios from 'axios';

import type { SerialNumberActivitySource } from './constants';

export type SerialNumber = {
  id?: number | null;
  unitIndex: number | null;
  serialName: string | null;
  serialNumber: string;
};

export type SerialNumbersLoadResponse = {
  orderItemId: number;
  serialNumbers: SerialNumber[];
};

const sourceParams = (source?: SerialNumberActivitySource) => (source ? { params: { activitySource: source } } : undefined);

export const loadSerialNumbersForFulfillmentPlan = async (fulfillmentPlanId: number): Promise<SerialNumbersLoadResponse[]> => {
  const response = await axios.get<SerialNumbersLoadResponse[]>(`/order/${fulfillmentPlanId}/serial-numbers`);
  return response.data ?? [];
};

export const createSerialNumber = async (
  fulfillmentPlanId: number,
  orderItemId: number,
  serial: SerialNumber,
  source?: SerialNumberActivitySource,
): Promise<SerialNumber> => {
  const response = await axios.post<SerialNumber>(
    `/order/${fulfillmentPlanId}/item/${orderItemId}/serial-number`,
    serial,
    sourceParams(source),
  );
  return response.data;
};

export const updateSerialNumber = async (
  fulfillmentPlanId: number,
  orderItemId: number,
  id: number,
  serial: SerialNumber,
  source?: SerialNumberActivitySource,
): Promise<void> => {
  await axios.put(`/order/${fulfillmentPlanId}/item/${orderItemId}/serial-number/${id}`, serial, sourceParams(source));
};

export const deleteSerialNumber = async (
  fulfillmentPlanId: number,
  orderItemId: number,
  id: number,
  source?: SerialNumberActivitySource,
): Promise<void> => {
  await axios.delete(`/order/${fulfillmentPlanId}/item/${orderItemId}/serial-number/${id}`, sourceParams(source));
};

// Syncs the updated list of serial numbers against the saved state using CRUD operations.
// Creates new entries, updates changed ones (matched by id), and deletes removed ones.
export const syncSerialNumbers = async (
  fulfillmentPlanId: number,
  orderItemId: number,
  previous: readonly SerialNumber[],
  updated: SerialNumber[],
  source?: SerialNumberActivitySource,
): Promise<void> => {
  const previousById = new Map(previous.filter((s) => s.id != null).map((s) => [s.id!, s]));
  const updatedById = new Map(updated.filter((s) => s.id != null).map((s) => [s.id!, s]));

  const creates = updated.filter((s) => s.id == null);
  const updates = updated.filter((s) => s.id != null && previousById.has(s.id!));
  const deletes = previous.filter((s) => s.id != null && !updatedById.has(s.id!));

  await Promise.all([
    ...creates.map((s) => createSerialNumber(fulfillmentPlanId, orderItemId, s, source)),
    ...updates.map((s) => updateSerialNumber(fulfillmentPlanId, orderItemId, s.id!, s, source)),
    ...deletes.map((s) => deleteSerialNumber(fulfillmentPlanId, orderItemId, s.id!, source)),
  ]);
};

// Bulk sync via single PUT — backend handles insert/update/delete atomically.
// Send all current serials (with id for existing, without for new); omit deleted ones.
export const bulkSyncSerialNumbers = async (
  fulfillmentPlanId: number,
  orderItemId: number,
  serialNumbers: SerialNumber[],
  source?: SerialNumberActivitySource,
): Promise<SerialNumber[]> => {
  const response = await axios.put<SerialNumber[]>(
    `/order/${fulfillmentPlanId}/item/${orderItemId}/serial-numbers`,
    serialNumbers,
    sourceParams(source),
  );
  return response.data;
};
